// ─── Direct provider sign-in (no backend required) ───────────
// Google and Microsoft both support browser-side OAuth for single-page apps, so
// calendars can be linked by consenting on the provider's own domain — no
// password ever reaches this app and no client secret is shipped to the browser.
//
//   Google    → Google Identity Services token client (implicit). Google does
//               not issue refresh tokens to browser clients, so access tokens
//               last ~1 hour and are renewed silently while the Google session
//               is alive.
//   Microsoft → Authorization Code + PKCE against a redirect URI registered
//               under the "Single-page application" platform, which is the
//               configuration Azure serves CORS headers for. Returns a rotating
//               refresh token, so the link survives reloads.
//
// A deployed planner backend is still the better home for *scheduled* sync
// (nightly pulls, push notifications) — this covers on-demand sync from the app.

export type OAuthProvider = 'google' | 'microsoft'

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
export const MS_CLIENT_ID = import.meta.env.VITE_MS_CLIENT_ID || ''
/** 'common' covers both personal and work/school accounts. */
export const MS_TENANT = import.meta.env.VITE_MS_TENANT || 'common'

// Read-only on purpose: the planner shows your calendars, it does not edit them.
export const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar.readonly'
export const MS_SCOPES = 'openid profile offline_access User.Read Calendars.Read'

export function isProviderConfigured(provider: OAuthProvider): boolean {
  return provider === 'google' ? !!GOOGLE_CLIENT_ID : !!MS_CLIENT_ID
}

export interface OAuthSession {
  provider: OAuthProvider
  accessToken: string
  /** Epoch ms. Treated as expired a minute early to avoid racing the clock. */
  expiresAt: number
  refreshToken?: string
  accountEmail: string
  scope: string
  connectedAt: string
}

// ─── Session storage ─────────────────────────────────────────
// Tokens live in localStorage so a linked account survives a reload. They are
// bearer credentials for read-only calendar access; "Disconnect" clears them and
// asks the provider to revoke where that is supported.

const SESSION_KEY = 'nb-planner-oauth-v1'

type SessionMap = Partial<Record<OAuthProvider, OAuthSession>>

function loadSessions(): SessionMap {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as SessionMap) : {}
  } catch {
    return {}
  }
}

function persist(sessions: SessionMap) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessions))
  } catch {
    // storage unavailable — the session lasts as long as the page does
  }
}

export function getSession(provider: OAuthProvider): OAuthSession | null {
  return loadSessions()[provider] ?? null
}

export function listSessions(): OAuthSession[] {
  return Object.values(loadSessions()).filter(Boolean) as OAuthSession[]
}

function saveSession(session: OAuthSession) {
  const sessions = loadSessions()
  sessions[session.provider] = session
  persist(sessions)
}

export function clearSession(provider: OAuthProvider) {
  const sessions = loadSessions()
  delete sessions[provider]
  persist(sessions)
}

// ─── PKCE helpers ────────────────────────────────────────────

function randomString(bytes = 48): string {
  const buf = new Uint8Array(bytes)
  crypto.getRandomValues(buf)
  return base64Url(buf)
}

function base64Url(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes
  let binary = ''
  view.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(digest)
}

// ─── Google (GIS token client) ───────────────────────────────

interface GisTokenResponse {
  access_token?: string
  expires_in?: number
  scope?: string
  error?: string
  error_description?: string
}

interface GisTokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string
            scope: string
            callback: (response: GisTokenResponse) => void
            error_callback?: (error: { type?: string; message?: string }) => void
          }) => GisTokenClient
          revoke: (token: string, done: () => void) => void
        }
      }
    }
  }
}

let gisLoader: Promise<void> | null = null

function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve()
  if (gisLoader) return gisLoader
  gisLoader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      gisLoader = null
      reject(new Error('Could not load Google sign-in. Check the network connection and any content blockers.'))
    }
    document.head.appendChild(script)
  })
  return gisLoader
}

/**
 * `prompt: ''` asks Google to answer silently when the browser still has an
 * active Google session and the scopes were granted before — that is the token
 * renewal path. `prompt: 'consent'` forces the account picker for a fresh link.
 */
async function requestGoogleToken(prompt: '' | 'consent'): Promise<GisTokenResponse> {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error(
      'Google is not set up for this build. Add VITE_GOOGLE_CLIENT_ID (see docs/family-planner-architecture.md).',
    )
  }
  await loadGis()
  return new Promise<GisTokenResponse>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error))
          return
        }
        resolve(response)
      },
      error_callback: (error) => {
        reject(
          new Error(
            error.type === 'popup_closed'
              ? 'Google sign-in was closed before it finished.'
              : error.message || 'Google sign-in failed.',
          ),
        )
      },
    })
    client.requestAccessToken({ prompt })
  })
}

/**
 * The primary calendar's id is the account's own address, so the linked account
 * can be labelled without asking for a profile scope on top of calendar access.
 */
async function googleAccountEmail(accessToken: string): Promise<string> {
  try {
    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/calendars/primary?fields=id',
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    if (!response.ok) return 'Google account'
    const body = (await response.json()) as { id?: string }
    return body.id || 'Google account'
  } catch {
    return 'Google account'
  }
}

export async function connectGoogle(): Promise<OAuthSession> {
  const token = await requestGoogleToken('consent')
  if (!token.access_token) throw new Error('Google did not return an access token.')
  const session: OAuthSession = {
    provider: 'google',
    accessToken: token.access_token,
    expiresAt: Date.now() + (token.expires_in ?? 3600) * 1000,
    accountEmail: await googleAccountEmail(token.access_token),
    scope: token.scope ?? GOOGLE_SCOPES,
    connectedAt: new Date().toISOString(),
  }
  saveSession(session)
  return session
}

// ─── Microsoft (auth code + PKCE, full-page redirect) ────────

const MS_PENDING_KEY = 'nb-planner-ms-pending'

interface MsPending {
  verifier: string
  state: string
  redirectUri: string
}

function msAuthority(path: 'authorize' | 'token'): string {
  return `https://login.microsoftonline.com/${encodeURIComponent(MS_TENANT)}/oauth2/v2.0/${path}`
}

/** Azure matches this against the registered SPA redirect URI exactly. */
function msRedirectUri(): string {
  return `${window.location.origin}${window.location.pathname}`
}

/** Sends the browser to Microsoft. Resolves only if the redirect fails to start. */
export async function connectMicrosoft(): Promise<never> {
  if (!MS_CLIENT_ID) {
    throw new Error(
      'Microsoft is not set up for this build. Add VITE_MS_CLIENT_ID (see docs/family-planner-architecture.md).',
    )
  }
  const verifier = randomString()
  const state = randomString(16)
  const redirectUri = msRedirectUri()
  const pending: MsPending = { verifier, state, redirectUri }
  sessionStorage.setItem(MS_PENDING_KEY, JSON.stringify(pending))

  const params = new URLSearchParams({
    client_id: MS_CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri,
    response_mode: 'query',
    scope: MS_SCOPES,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
    prompt: 'select_account',
  })
  window.location.assign(`${msAuthority('authorize')}?${params}`)
  return new Promise<never>(() => {}) // navigation takes over
}

interface MsTokenResponse {
  access_token?: string
  refresh_token?: string
  expires_in?: number
  scope?: string
  error?: string
  error_description?: string
}

async function msTokenRequest(body: Record<string, string>): Promise<MsTokenResponse> {
  const response = await fetch(msAuthority('token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: MS_CLIENT_ID, ...body }),
  })
  const payload = (await response.json()) as MsTokenResponse
  if (!response.ok || payload.error) {
    throw new Error(payload.error_description || payload.error || `Microsoft sign-in failed (${response.status}).`)
  }
  return payload
}

async function msAccountEmail(accessToken: string): Promise<string> {
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=mail,userPrincipalName', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!response.ok) return 'Microsoft account'
    const body = (await response.json()) as { mail?: string; userPrincipalName?: string }
    return body.mail || body.userPrincipalName || 'Microsoft account'
  } catch {
    return 'Microsoft account'
  }
}

/**
 * Call once on mount. Returns the new session when the page was just reached by
 * Microsoft's redirect, null otherwise. The query string is cleaned up either
 * way so a reload can't replay a consumed code.
 */
export async function completeMicrosoftRedirect(): Promise<OAuthSession | null> {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const state = params.get('state')
  const error = params.get('error')
  const rawPending = sessionStorage.getItem(MS_PENDING_KEY)
  if (!rawPending || (!code && !error)) return null

  sessionStorage.removeItem(MS_PENDING_KEY)
  const cleanUrl = `${window.location.pathname}${window.location.hash}`
  window.history.replaceState({}, '', cleanUrl)

  if (error) {
    throw new Error(params.get('error_description') || `Microsoft declined the sign-in (${error}).`)
  }

  const pending = JSON.parse(rawPending) as MsPending
  if (state !== pending.state) {
    throw new Error('Microsoft sign-in could not be verified (state mismatch). Please try again.')
  }

  const token = await msTokenRequest({
    grant_type: 'authorization_code',
    code: code!,
    redirect_uri: pending.redirectUri,
    code_verifier: pending.verifier,
    scope: MS_SCOPES,
  })
  if (!token.access_token) throw new Error('Microsoft did not return an access token.')

  const session: OAuthSession = {
    provider: 'microsoft',
    accessToken: token.access_token,
    expiresAt: Date.now() + (token.expires_in ?? 3600) * 1000,
    refreshToken: token.refresh_token,
    accountEmail: await msAccountEmail(token.access_token),
    scope: token.scope ?? MS_SCOPES,
    connectedAt: new Date().toISOString(),
  }
  saveSession(session)
  return session
}

async function refreshMicrosoft(session: OAuthSession): Promise<OAuthSession> {
  if (!session.refreshToken) {
    throw new Error('The Microsoft link expired. Reconnect the account on the Sources tab.')
  }
  const token = await msTokenRequest({
    grant_type: 'refresh_token',
    refresh_token: session.refreshToken,
    scope: MS_SCOPES,
  })
  if (!token.access_token) throw new Error('Microsoft did not return a refreshed token.')
  const refreshed: OAuthSession = {
    ...session,
    accessToken: token.access_token,
    expiresAt: Date.now() + (token.expires_in ?? 3600) * 1000,
    // Microsoft rotates refresh tokens; keep the newest one.
    refreshToken: token.refresh_token ?? session.refreshToken,
  }
  saveSession(refreshed)
  return refreshed
}

// ─── Token access ────────────────────────────────────────────

const EXPIRY_SKEW_MS = 60_000

/** A valid access token for the provider, renewing it silently if needed. */
export async function getAccessToken(provider: OAuthProvider): Promise<string> {
  const session = getSession(provider)
  if (!session) {
    throw new Error(`No ${provider === 'google' ? 'Google' : 'Microsoft'} account is linked.`)
  }
  if (session.expiresAt - EXPIRY_SKEW_MS > Date.now()) return session.accessToken

  if (provider === 'microsoft') {
    return (await refreshMicrosoft(session)).accessToken
  }

  // Google browser clients get no refresh token — ask GIS for a silent renewal.
  const token = await requestGoogleToken('')
  if (!token.access_token) {
    throw new Error('The Google link expired. Reconnect the account on the Sources tab.')
  }
  saveSession({
    ...session,
    accessToken: token.access_token,
    expiresAt: Date.now() + (token.expires_in ?? 3600) * 1000,
    scope: token.scope ?? session.scope,
  })
  return token.access_token
}

/** Clears the stored tokens and, for Google, asks the provider to revoke them. */
export async function revokeProvider(provider: OAuthProvider): Promise<void> {
  const session = getSession(provider)
  clearSession(provider)
  if (!session) return
  if (provider === 'google' && window.google?.accounts?.oauth2) {
    await new Promise<void>((resolve) => {
      try {
        window.google!.accounts.oauth2.revoke(session.accessToken, resolve)
      } catch {
        resolve()
      }
    })
  }
  // Microsoft has no browser-callable revoke endpoint; dropping the refresh
  // token ends this app's access. Full sign-out lives in the Microsoft account
  // security page, linked from the Sources tab.
}
